import React, { useState } from 'react';
import { DB, uploadImage } from '../data/database';
import { useLanguage } from '../translations/LanguageContext';
import { exportCSV } from '../data/constants';

const genId = () => 'TA-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const timeAgo = (d) => {
  if (!d) return '';
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return Math.floor(ms / 60000) + ' min ago';
  if (ms < 86400000) return Math.floor(ms / 3600000) + 'h ago';
  if (ms < 604800000) return Math.floor(ms / 86400000) + 'd ago';
  return Math.floor(ms / 604800000) + 'w ago';
};

const STATUS_BADGE = {
  pending: { bg: 'rgba(234,179,8,0.12)', color: '#D97706', label: 'PENDING' },
  under_review: { bg: 'rgba(59,130,246,0.12)', color: '#2563EB', label: 'UNDER REVIEW' },
  approved: { bg: 'rgba(34,197,94,0.12)', color: '#16A34A', label: '✓ APPROVED' },
  rejected: { bg: 'rgba(239,68,68,0.12)', color: '#DC2626', label: 'REJECTED' },
};

const BIZ_TYPES = ['dealer', 'garage', 'exporter', 'broker', 'fleet', 'other'];
const VOLUMES = [
  { id: 'less_than_5', label: 'Less than 5' },
  { id: '5_to_20', label: '5–20 units' },
  { id: '20_to_50', label: '20–50 units' },
  { id: '50_plus', label: '50+ units' },
];
const DOC_TYPES = ['trade_license', 'company_registration', 'tax_certificate', 'id_document', 'other'];
const REJECTION_REASONS = ['Incomplete documents', 'Invalid trade license', 'Could not verify business', 'Duplicate application', 'Other'];

function TradeApplications({ user }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState('pending');
  const [detail, setDetail] = useState(null);
  const [showApprove, setShowApprove] = useState(null);
  const [showReject, setShowReject] = useState(null);
  const [docViewer, setDocViewer] = useState(null);
  const [, forceUpdate] = useState();

  const apps = DB.get('tradeApplications') || [];
  const customers = DB.get('customers') || [];

  const save = (list) => { DB.set('tradeApplications', list); forceUpdate({}); };
  const updateApp = (id, updates) => {
    save(apps.map(a => a.id === id ? { ...a, ...updates, updated_at: new Date().toISOString() } : a));
    if (detail?.id === id) setDetail(prev => ({ ...prev, ...updates }));
  };

  const pending = apps.filter(a => a.status === 'pending' || a.status === 'under_review');
  const approved = apps.filter(a => a.status === 'approved');
  const rejected = apps.filter(a => a.status === 'rejected');
  const tabItems = tab === 'pending' ? pending : tab === 'approved' ? approved : rejected;

  const handleExport = () => {
    const data = approved.map(a => ({
      business_name: a.business_name, city: a.business_city, country: a.business_country,
      contact_name: a.contact_name, email: a.contact_email, phone: a.contact_phone,
      license_number: a.license_number, approved_date: a.reviewed_at, customer_id: a.customer_id || '',
    }));
    exportCSV(data, `trade-dealers-approved-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  /* ══════════ APPROVE MODAL ══════════ */
  const ApproveModal = () => {
    const app = showApprove;
    if (!app) return null;
    const [f, setF] = useState({
      name: app.business_name, contact: app.contact_name, email: app.contact_email,
      phone: app.contact_phone || '', country: app.business_country, currency: 'USD',
      terms: '50/50', notes: '',
    });
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const handleApprove = () => {
      const password = Math.random().toString(36).slice(2, 10);
      const newCust = {
        id: DB.genId('CUS'), name: f.name, email: f.email, phone: f.phone,
        country: f.country, company: f.name, password, status: 'active',
        is_trade: true, payment_terms: f.terms, preferred_currency: f.currency,
        createdAt: new Date().toISOString(),
      };
      const custList = DB.get('customers') || [];
      DB.set('customers', [...custList, newCust]);

      updateApp(app.id, {
        status: 'approved', customer_id: newCust.id,
        reviewed_by: user.id, reviewed_by_name: user.name,
        reviewed_at: new Date().toISOString(), review_notes: f.notes,
      });
      setShowApprove(null);
      setDetail(null);

      // Show credentials
      const waNum = (f.phone || '').replace(/[^\d]/g, '');
      const waMsg = encodeURIComponent(`Congratulations! Your trade application for ${f.name} has been approved.\n\nPortal login:\nEmail: ${f.email}\nPassword: ${password}\n\nLog in at: ${window.location.origin}`);
      alert(`✅ Approved! Customer account created.\n\nLogin: ${f.email}\nPassword: ${password}\n\nA WhatsApp window will open to send credentials.`);
      if (waNum) window.open(`https://wa.me/${waNum}?text=${waMsg}`, '_blank');
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowApprove(null)}>
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, background: 'var(--bg1)', borderRadius: 16, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>Approve Application</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{app.business_name} · {app.business_country}</div>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ padding: 14, background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, marginBottom: 20, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
              This will:<br />✅ Create a customer account for this business<br />✅ Generate login credentials<br />✅ Grant access to trade pricing and portal
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label className="form-label" style={{ fontSize: 11 }}>Company Name</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} /></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Contact Name</label><input className="form-input" value={f.contact} onChange={e => set('contact', e.target.value)} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label className="form-label" style={{ fontSize: 11 }}>Email (login)</label><input className="form-input" value={f.email} onChange={e => set('email', e.target.value)} /></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Phone</label><input className="form-input" value={f.phone} onChange={e => set('phone', e.target.value)} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label className="form-label" style={{ fontSize: 11 }}>Country</label><input className="form-input" value={f.country} onChange={e => set('country', e.target.value)} /></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Currency</label><select className="form-input" value={f.currency} onChange={e => set('currency', e.target.value)}><option>USD</option><option>AED</option><option>JPY</option><option>EUR</option></select></div>
              <div><label className="form-label" style={{ fontSize: 11 }}>Payment Terms</label><select className="form-input" value={f.terms} onChange={e => set('terms', e.target.value)}><option value="50/50">50/50</option><option value="full_advance">Full Advance</option><option value="30/70">30/70</option><option value="net_30">Net 30</option></select></div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Admin Notes (internal)</label>
              <textarea className="form-input" rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Good references, verified dealer..." />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowApprove(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: 'var(--green)' }} onClick={handleApprove}>✓ Approve & Create Account</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ REJECT MODAL ══════════ */
  const RejectModal = () => {
    const app = showReject;
    if (!app) return null;
    const [reason, setReason] = useState('Incomplete documents');
    const [notes, setNotes] = useState('');
    const [notify, setNotify] = useState(true);

    const handleReject = () => {
      updateApp(app.id, {
        status: 'rejected', rejection_reason: reason, review_notes: notes,
        reviewed_by: user.id, reviewed_by_name: user.name,
        reviewed_at: new Date().toISOString(),
      });
      if (notify && app.contact_email) {
        const waNum = (app.contact_phone || '').replace(/[^\d]/g, '');
        const waMsg = encodeURIComponent(`Regarding your trade application for ${app.business_name}: Unfortunately, we were unable to approve your application at this time.\n\nReason: ${reason}\n${notes ? '\n' + notes : ''}\n\nYou are welcome to reapply with updated documents.`);
        if (waNum) window.open(`https://wa.me/${waNum}?text=${waMsg}`, '_blank');
      }
      setShowReject(null);
      setDetail(null);
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowReject(null)}>
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, background: 'var(--bg1)', borderRadius: 16, border: '1px solid var(--border)' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text1)' }}>Reject Application</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{app.business_name}</div>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 14 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Reason for rejection</label>
              <select className="form-input" value={reason} onChange={e => setReason(e.target.value)}>
                {REJECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label className="form-label" style={{ fontSize: 11 }}>Additional notes to applicant</label>
              <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Please resubmit with a valid trade license..." />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text2)', marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} style={{ accentColor: 'var(--blue)' }} />
              Notify applicant via WhatsApp
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowReject(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#DC2626' }} onClick={handleReject}>Reject Application</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ DOCUMENT VIEWER MODAL ══════════ */
  const DocViewerModal = () => {
    if (!docViewer) return null;
    const { docs, appName } = docViewer;
    const [activeDoc, setActiveDoc] = useState(0);
    const doc = docs[activeDoc];
    const isImage = doc?.url && /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.url);
    const isPDF = doc?.url && /\.pdf$/i.test(doc.url);

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setDocViewer(null)}>
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 800, maxHeight: '90vh', background: 'var(--bg1)', borderRadius: 16, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text1)' }}>Documents — {appName}</div>
            <button onClick={() => setDocViewer(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>
          {/* Preview */}
          <div style={{ flex: 1, minHeight: 300, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            {doc?.url ? (
              isImage ? <img src={doc.url} alt={doc.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : isPDF ? <iframe src={doc.url} title={doc.name} style={{ width: '100%', height: '100%', border: 'none', minHeight: 400 }} />
              : <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Preview not available for this file type.<br /><a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>Download file</a></div>
            ) : <div style={{ color: 'var(--text3)' }}>No document selected</div>}
          </div>
          {/* Document list */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
            {docs.map((d, i) => (
              <div key={d.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < docs.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', background: i === activeDoc ? 'var(--bg2)' : 'transparent', borderRadius: 6, padding: '8px 10px', marginBottom: 2 }} onClick={() => setActiveDoc(i)}>
              <span style={{ fontSize: 16 }}>{/\.(jpg|jpeg|png|gif|webp)$/i.test(d.url || '') ? '🖼️' : '📄'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{d.name || 'Document'}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{d.type?.replace(/_/g, ' ')} · {timeAgo(d.uploaded_at)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={e => { e.stopPropagation(); setActiveDoc(i); }} className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}>VIEW</button>
                {d.url && <a href={d.url} download target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px', textDecoration: 'none' }}>↓</a>}
              </div>
            </div>
            ))}
            {docs.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No documents uploaded.</div>}
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ DETAIL VIEW ══════════ */
  const DetailView = () => {
    const app = detail;
    if (!app) return null;
    const [notes, setNotes] = useState(app.review_notes || '');
    const badge = STATUS_BADGE[app.status] || STATUS_BADGE.pending;

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setDetail(null)}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 580, background: 'var(--bg1)', height: '100%', overflowY: 'auto', position: 'relative', zIndex: 1, borderLeft: '1px solid var(--border)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'var(--bg1)', zIndex: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>Buyer Application</span>
            <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>

          <div style={{ padding: 20 }}>
            {/* Business details */}
            <Section title="Business Details">
              <Row label="Business" value={app.business_name} bold />
              <Row label="City" value={app.business_city} />
              <Row label="Country" value={app.business_country} />
              <Row label="Type" value={app.business_type} cap />
              <Row label="Monthly Volume" value={VOLUMES.find(v => v.id === app.monthly_volume)?.label} />
              {app.interested_in?.length > 0 && <Row label="Interested In" value={app.interested_in.join(', ')} cap />}
            </Section>

            {/* Contact */}
            <Section title="Contact">
              <Row label="Name" value={`${app.contact_name}${app.contact_position ? ' (' + app.contact_position + ')' : ''}`} bold />
              <Row label="Email" value={app.contact_email} />
              <Row label="Phone" value={app.contact_phone} />
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {app.contact_phone && <a href={`https://wa.me/${(app.contact_phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📱 WhatsApp</a>}
                {app.contact_email && <a href={`mailto:${app.contact_email}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📧 Email</a>}
                {app.contact_phone && <a href={`tel:${app.contact_phone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>📞 Call</a>}
              </div>
            </Section>

            {/* Documents */}
            <Section title="Documents">
              {app.license_type && <Row label="License" value={`${app.license_type} ${app.license_number || ''}`} />}
              {(app.documents || []).length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  {app.documents.map((d, i) => (
                    <div key={d.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span>{/\.(jpg|jpeg|png)$/i.test(d.url || '') ? '🖼️' : '📄'}</span>
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--text1)' }}>{d.name}</span>
                      <button onClick={() => setDocViewer({ docs: app.documents, appName: app.business_name })} className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px' }}>VIEW</button>
                      {d.url && <a href={d.url} download target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 8px', textDecoration: 'none' }}>↓</a>}
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>No documents uploaded.</div>}
              {(app.documents || []).length > 0 && (
                <button onClick={() => setDocViewer({ docs: app.documents, appName: app.business_name })} className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}>📄 View All Documents</button>
              )}
            </Section>

            {/* Applicant's note */}
            {app.notes && (
              <Section title="Application Note">
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, fontStyle: 'italic' }}>"{app.notes}"</div>
              </Section>
            )}

            {/* Admin notes */}
            <Section title="Admin Notes">
              <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add internal notes here..." style={{ fontSize: 13 }} />
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => updateApp(app.id, { review_notes: notes })}>Save Notes</button>
            </Section>

            {/* Status */}
            <Section title="Status">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ padding: '4px 12px', borderRadius: 6, background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 700 }}>{badge.label}</span>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Submitted {timeAgo(app.created_at)}</span>
              </div>
              {app.reviewed_by_name && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Reviewed by {app.reviewed_by_name} · {timeAgo(app.reviewed_at)}</div>}
              {app.rejection_reason && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>Reason: {app.rejection_reason}</div>}
              {app.customer_id && (
                <div style={{ marginTop: 8, padding: 10, background: 'rgba(34,197,94,0.06)', borderRadius: 8, fontSize: 12, color: 'var(--green)' }}>
                  ✅ Customer account created (ID: {app.customer_id})
                </div>
              )}
            </Section>

            {/* Actions */}
            {(app.status === 'pending' || app.status === 'under_review') && (
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <button className="btn btn-secondary" style={{ color: '#DC2626', borderColor: '#DC2626' }} onClick={() => { setDetail(null); setShowReject(app); }}>Reject</button>
                <button className="btn btn-primary" style={{ background: 'var(--green)', flex: 1 }} onClick={() => { setDetail(null); setShowApprove(app); }}>✓ Approve</button>
              </div>
            )}
            {app.status === 'rejected' && (
              <button className="btn btn-secondary" style={{ marginTop: 20, width: '100%' }} onClick={() => updateApp(app.id, { status: 'pending', rejection_reason: '', review_notes: '' })}>Re-open Application</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ══════════ MAIN RENDER ══════════ */
  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Sales</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Buyer Applications</h1>
            <p style={{ fontSize: 13, color: 'var(--text3)' }}>Approved buyers get portal access, pricing, and dedicated support.</p>
          </div>
          {approved.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={handleExport}>↓ Export approved</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[
          { id: 'pending', label: 'Pending', count: pending.length },
          { id: 'approved', label: 'Approved', count: approved.length },
          { id: 'rejected', label: 'Rejected', count: rejected.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`tab ${tab === t.id ? 'active' : ''}`} style={{ fontSize: 13 }}>
            {t.label} <span className="cnt">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      {tabItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text3)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{tab === 'pending' ? '📋' : tab === 'approved' ? '✅' : '❌'}</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>No {tab} applications</div>
          <div style={{ fontSize: 13 }}>{tab === 'pending' ? 'New applications will appear here when businesses apply through the website.' : ''}</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Business', 'Country', 'License', 'Contact', 'Submitted', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tabItems.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map(app => {
                const badge = STATUS_BADGE[app.status] || STATUS_BADGE.pending;
                const docs = app.documents || [];
                return (
                  <tr key={app.id} onClick={() => setDetail(app)} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{app.business_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{app.business_city}{app.business_type ? ` · ${app.business_type}` : ''}</div>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--text2)' }}>{app.business_country}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {app.license_type && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{app.license_type} {app.license_number}</div>}
                      {docs.length > 0 && (
                        <button onClick={e => { e.stopPropagation(); setDocViewer({ docs, appName: app.business_name }); }} style={{ fontSize: 11, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }}>
                          📄 View {docs.length} document{docs.length !== 1 ? 's' : ''} →
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 500, color: 'var(--text1)' }}>{app.contact_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{app.contact_email}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text3)' }}>{timeAgo(app.created_at)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 6, background: badge.bg, color: badge.color, fontSize: 10, fontWeight: 700 }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
                      {(app.status === 'pending' || app.status === 'under_review') && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" style={{ color: '#DC2626', borderColor: '#DC2626', fontSize: 11, padding: '4px 12px' }} onClick={() => setShowReject(app)}>Reject</button>
                          <button className="btn btn-primary btn-sm" style={{ background: 'var(--green)', fontSize: 11, padding: '4px 12px' }} onClick={() => setShowApprove(app)}>✓ Approve</button>
                        </div>
                      )}
                      {app.status === 'approved' && app.customer_id && (
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>Customer linked</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {detail && <DetailView />}
      {showApprove && <ApproveModal />}
      {showReject && <RejectModal />}
      {docViewer && <DocViewerModal />}
    </div>
  );
}

/* ── Shared detail sub-components ── */
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10, borderTop: '1px solid var(--border)', paddingTop: 16 }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value, bold, cap }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--text3)' }}>{label}</span>
      <span style={{ color: 'var(--text1)', fontWeight: bold ? 600 : 400, textTransform: cap ? 'capitalize' : 'none' }}>{value}</span>
    </div>
  );
}

export default TradeApplications;
